import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import Button from '@datacamp/waffles-button';
import { CodeIcon } from '@datacamp/waffles-icons';
import { spacing } from '@datacamp/waffles-tokens';
import cx from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import Rating from '../../FeedbackRating';
import { unreachableWithReturn } from '../../unreachable';
import { useTranslation } from '../../useTranslation';
import Title from '../Title';
import { useStreamPrediction } from '../useStreamPrediction';
import { appendClassSuffix } from '../utils';
import AIAssistantButton from './Button';
import AIAssistantRating from './Rating';

var CampusBody = function CampusBody(_ref) {
  var Renderer = _ref.Renderer,
      body = _ref.body,
      title = _ref.title;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(Title, {
    title: title
  }), /*#__PURE__*/React.createElement(Renderer, {
    markdown: body,
    theme: 'ASSISTANT'
  }));
};

var ShowSolutionButtonAndCopy = function ShowSolutionButtonAndCopy(_ref2) {
  var onClick = _ref2.onClick;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var cy = 'exercise-show-solution-incorrect-submission';
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
    style: {
      marginInline: spacing.medium
    }
  }, t('AiAssistantBody.showSolutionCopy')), /*#__PURE__*/React.createElement(Button, {
    className: "dc-sct-feedback__nav--hint-solution",
    css: {
      marginBottom: spacing.medium,
      marginInline: spacing.medium
    },
    dataAttributes: {
      cy: cy
    },
    onClick: onClick,
    size: "small",
    tooltipPosition: "top",
    tooltipText: "Ctrl+H"
  }, /*#__PURE__*/React.createElement(CodeIcon, null), "".concat(t('HintAndSolution.showAnswer'), " (-70 XP)")));
};

var Body = function Body(_ref3) {
  var Renderer = _ref3.Renderer,
      aiAssistantProps = _ref3.aiAssistantProps,
      body = _ref3.body,
      children = _ref3.children,
      isActive = _ref3.isActive,
      isSolutionShown = _ref3.isSolutionShown,
      isUserEnabledForAISubmissionHint = _ref3.isUserEnabledForAISubmissionHint,
      messageType = _ref3.messageType,
      _ref3$onClickShowSolu = _ref3.onClickShowSolution,
      onClickShowSolution = _ref3$onClickShowSolu === void 0 ? function () {} : _ref3$onClickShowSolu,
      predictions = _ref3.predictions,
      sctRating = _ref3.sctRating,
      setPrediction = _ref3.setPrediction,
      setPredictionRated = _ref3.setPredictionRated,
      initialTitle = _ref3.title,
      uuid = _ref3.uuid;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      feedbackProvided = _useState2[0],
      setFeedbackProvided = _useState2[1];

  var _useState3 = useState(undefined),
      _useState4 = _slicedToArray(_useState3, 2),
      predictionId = _useState4[0],
      setPredictionId = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      rated = _useState6[0],
      setRated = _useState6[1];

  var _useState7 = useState({
    content: '',
    type: 'initial'
  }),
      _useState8 = _slicedToArray(_useState7, 2),
      streamingData = _useState8[0],
      setStreamingData = _useState8[1];

  var _useTranslation2 = useTranslation(),
      t = _useTranslation2.t;

  var lastPrediction = useMemo(function () {
    return predictions.current[uuid];
  }, [predictions, uuid]);
  useEffect(function () {
    var content = lastPrediction ? lastPrediction.content : body;
    setStreamingData({
      content: content,
      type: 'initial'
    });

    if (lastPrediction) {
      setFeedbackProvided(true);
      setPredictionId(lastPrediction.id);
      setRated(lastPrediction.rated || false);
    }
  }, [body, lastPrediction, predictions, uuid]);

  var _useStreamPrediction = useStreamPrediction({
    aiAssistantProps: aiAssistantProps,
    messageType: messageType,
    setStreamingData: setStreamingData
  }),
      getPrediction = _useStreamPrediction.getPrediction,
      loading = _useStreamPrediction.loading;

  useEffect(function () {
    if (streamingData.type === 'ready') {
      setPrediction({
        content: streamingData.content,
        id: streamingData.id,
        rated: false,
        uuid: uuid || ''
      }, uuid);
      setFeedbackProvided(true);
      setPredictionId(streamingData.id);
      setRated(false);
      setStreamingData({
        content: streamingData.content,
        type: 'initial'
      });

      if (messageType === 'INCORRECT' && aiAssistantProps) {
        aiAssistantProps.setHintShown(true);
        aiAssistantProps.setUserUsedAiIncorrectSubmissions(true);
      } else if (messageType === 'ASSISTANT' && aiAssistantProps) {
        aiAssistantProps.setUserUsedAiErrorExplanation(true);
      }
    }
  }, [aiAssistantProps, messageType, setPrediction, streamingData, uuid]);
  var aiAssistantButton = /*#__PURE__*/React.createElement(AIAssistantButton, {
    loading: loading,
    messageType: messageType,
    onClick: /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return getPrediction();

            case 2:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }))
  });
  var shouldDisplayAiAssistantButton = !feedbackProvided && (messageType === 'ASSISTANT' || isUserEnabledForAISubmissionHint);
  var shouldShowSolutionButtonAndCopy = !isSolutionShown && messageType === 'INCORRECT' && feedbackProvided;
  var title = useMemo(function () {
    var shouldDisplayAiHintTitle = messageType === 'INCORRECT' && (streamingData.type !== 'initial' || feedbackProvided);

    if (shouldDisplayAiHintTitle) {
      return t('AiAssistantBody.aiHintTitle');
    }

    return initialTitle;
  }, [feedbackProvided, initialTitle, messageType, streamingData.type, t]);

  var renderStreamingData = function renderStreamingData() {
    switch (streamingData.type) {
      case 'initial':
        return /*#__PURE__*/React.createElement("div", {
          className: "dc-sct-feedback__content"
        }, children, title && /*#__PURE__*/React.createElement(CampusBody, {
          Renderer: Renderer,
          body: streamingData.content,
          title: title
        }), shouldShowSolutionButtonAndCopy && /*#__PURE__*/React.createElement(ShowSolutionButtonAndCopy, {
          onClick: onClickShowSolution
        }), shouldDisplayAiAssistantButton && aiAssistantButton);

      case 'ready':
        return /*#__PURE__*/React.createElement("div", {
          className: "dc-sct-feedback__content"
        }, children, title && streamingData.content && /*#__PURE__*/React.createElement(CampusBody, {
          Renderer: Renderer,
          body: streamingData.content,
          title: title
        }), shouldShowSolutionButtonAndCopy && /*#__PURE__*/React.createElement(ShowSolutionButtonAndCopy, {
          onClick: onClickShowSolution
        }));

      case 'streaming':
        return /*#__PURE__*/React.createElement("div", {
          className: "dc-sct-feedback__content"
        }, children, title && streamingData.content && /*#__PURE__*/React.createElement(CampusBody, {
          Renderer: Renderer,
          body: streamingData.content,
          title: title
        }));

      case 'error':
        return /*#__PURE__*/React.createElement("div", {
          className: "dc-sct-feedback__content"
        }, children, /*#__PURE__*/React.createElement(CampusBody, {
          Renderer: Renderer,
          body: t('AiAssistantBody.errorBody'),
          title: t('AiAssistantBody.errorTitle')
        }), shouldDisplayAiAssistantButton && aiAssistantButton);

      default:
        return unreachableWithReturn(streamingData, null);
    }
  };

  if (!isActive) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: cx('dc-sct-feedback__body', appendClassSuffix('dc-sct-feedback__body', messageType))
  }, renderStreamingData(), streamingData.type === 'initial' && !feedbackProvided && messageType === 'INCORRECT' && sctRating && /*#__PURE__*/React.createElement(Rating, sctRating), feedbackProvided && predictionId && aiAssistantProps && /*#__PURE__*/React.createElement(AIAssistantRating, {
    key: predictionId,
    messageType: messageType,
    predictionId: predictionId,
    rated: rated,
    saveScore: aiAssistantProps.saveScore,
    setPredictionRated: setPredictionRated,
    setRated: setRated,
    uuid: uuid
  }));
};

export default Body;